/**
 * KycSumsub — сбор KYC через встраиваемый WebSDK Sumsub (data_source=sumsub).
 * Всё собирает виджет Sumsub; мы монтируем SDK с access-токеном и опрашиваем статус.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import SumsubWebSdk from "@sumsub/websdk-react";

import { useBackButton, useMainButton } from "@/telegram/useMainButton";
import { FallbackButton, Screen } from "@/components/Screen";
import { friendlyError, kycApi, type KycStatus } from "@/lib/api";
import {
  ApprovedCard,
  AwaitingFinalCard,
  ErrorCard,
  RejectedCard,
  SkeletonBlock,
  SubmittedCard,
  resolveOutcome,
} from "@/screens/onboarding/kyc/shared";

type Props = { onNext: (status: KycStatus) => void; onBack: () => void };

type Stage =
  | { kind: "loading" }
  | { kind: "error"; message: string }
  | { kind: "sdk"; token: string }
  | { kind: "submitted"; latest?: KycStatus }
  | { kind: "awaiting"; latest?: KycStatus }
  | { kind: "approved"; latest: KycStatus }
  | { kind: "rejected"; latest: KycStatus };

export function KycSumsub({ onNext, onBack }: Props) {
  const [stage, setStage] = useState<Stage>({ kind: "loading" });
  const pollRef = useRef<number | null>(null);

  const fetchToken = useCallback(async (): Promise<string> => {
    const { token } = await kycApi.accessToken();
    return token;
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const status = await kycApi.status();
        const outcome = resolveOutcome(status);
        if (outcome === "approved") {
          setStage({ kind: "approved", latest: status });
          return;
        }
        if (outcome === "rejected") {
          setStage({ kind: "rejected", latest: status });
          return;
        }
        if (outcome === "awaiting") {
          // провайдер уже подтвердил — не запускаем новую проверку, ждём финал
          setStage({ kind: "awaiting", latest: status });
          startPolling();
          return;
        }
        const token = await fetchToken();
        setStage({ kind: "sdk", token });
      } catch (e) {
        setStage({ kind: "error", message: friendlyError(e) });
      }
    })();
  }, [fetchToken]);

  useEffect(() => {
    return () => {
      if (pollRef.current) window.clearInterval(pollRef.current);
    };
  }, []);

  const startPolling = useCallback(() => {
    if (pollRef.current) return;
    pollRef.current = window.setInterval(async () => {
      try {
        const s = await kycApi.status();
        const outcome = resolveOutcome(s);
        if (outcome === "approved") {
          if (pollRef.current) window.clearInterval(pollRef.current);
          pollRef.current = null;
          setStage({ kind: "approved", latest: s });
        } else if (outcome === "rejected") {
          if (pollRef.current) window.clearInterval(pollRef.current);
          pollRef.current = null;
          setStage({ kind: "rejected", latest: s });
        } else if (outcome === "awaiting") {
          setStage({ kind: "awaiting", latest: s });
        } else {
          setStage({ kind: "submitted", latest: s });
        }
      } catch {
        /* keep polling */
      }
    }, 3000);
  }, []);

  const onMessage = useCallback(
    (type: string, _payload: unknown) => {
      if (type === "idCheck.onApplicantSubmitted" || type === "idCheck.applicantReviewComplete") {
        setStage((cur) => (cur.kind === "sdk" ? { kind: "submitted" } : cur));
        startPolling();
      }
    },
    [startPolling],
  );

  const onError = useCallback((err: unknown) => {
    setStage({ kind: "error", message: err instanceof Error ? err.message : "Ошибка WebSDK" });
  }, []);

  const proceed = useCallback(() => {
    if (stage.kind === "approved") onNext(stage.latest);
  }, [stage, onNext]);

  useBackButton(stage.kind === "sdk" ? undefined : onBack);
  useMainButton({
    text: "Продолжить",
    onClick: proceed,
    visible: stage.kind === "approved",
    active: stage.kind === "approved",
  });

  return (
    <Screen
      title={
        stage.kind === "approved"
          ? "Верификация пройдена ✓"
          : stage.kind === "rejected"
          ? "Верификация не пройдена"
          : stage.kind === "awaiting"
          ? "Первичная проверка пройдена ✓"
          : stage.kind === "submitted"
          ? "Документы отправлены"
          : "Верификация личности"
      }
      subtitle={
        stage.kind === "sdk"
          ? "Сфотографируйте паспорт и сделайте короткое видео-селфи. Это нужно один раз."
          : stage.kind === "submitted"
          ? "Ожидаем результата проверки. Обычно занимает несколько минут."
          : undefined
      }
    >
      {stage.kind === "loading" && <SkeletonBlock />}

      {stage.kind === "error" && <ErrorCard message={stage.message} />}

      {stage.kind === "sdk" && (
        <div className="-mx-5 overflow-hidden">
          <SumsubWebSdk
            accessToken={stage.token}
            expirationHandler={fetchToken}
            config={{ lang: "ru" }}
            options={{ addViewportTag: false, adaptIframeHeight: true }}
            onMessage={onMessage}
            onError={onError}
          />
        </div>
      )}

      {stage.kind === "submitted" && <SubmittedCard text="Sumsub проверяет документы…" />}

      {stage.kind === "awaiting" && <AwaitingFinalCard />}

      {stage.kind === "approved" && <ApprovedCard />}

      {stage.kind === "rejected" && (
        <RejectedCard
          latest={stage.latest}
          onRetry={async () => {
            try {
              const token = await fetchToken();
              setStage({ kind: "sdk", token });
            } catch (e) {
              setStage({ kind: "error", message: friendlyError(e) });
            }
          }}
        />
      )}

      {stage.kind === "approved" && <FallbackButton text="Продолжить" onClick={proceed} />}
    </Screen>
  );
}
